/* Descrições e ontologia: cobertura, tabela paginada, fila de revisão.
 *
 * A marca de revisão vai para o servidor a cada clique. No painel antigo ela
 * morava no localStorage e sumia ao limpar cache, em outro navegador e em aba
 * anônima — em silêncio. */

import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { DescriptionRow, Paged } from "../lib/api";
import { cell, intBR, pct, shortName } from "../lib/format";
import { CoverageBars, type CoverageRow } from "../charts/CoverageBars";
import { Card, ChartFrame, Empty, ErrorState, PageHead, Skeleton, Tiles } from "../components/ui";
import { useActiveUser } from "../components/Shell";

interface Summary {
  rows: number; users: number; entities: number; without_ontology: number; flagged: number;
  types: CoverageRow[];
  extras: { key: string; rows: number }[];
  severity_spec: { rules: Record<string, string>; severity: string };
}

export function Ontology() {
  const userId = useActiveUser();
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();

  const category = params.get("categoria") || "";
  const status = params.get("status") || "all";
  const query = params.get("q") || "";
  const sort = params.get("ordem") || "amount";
  const page = Number(params.get("pagina") || 1);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "pagina") next.delete("pagina");
    setParams(next, { replace: true });
  };

  const summary = useQuery({
    queryKey: ["onto-summary"],
    queryFn: () => api<Summary>("/api/ontology/summary"),
  });
  const categories = useQuery({
    queryKey: ["onto-cats", userId],
    queryFn: () => api<{ category: string; flow: string; rows: number; amount: number;
                         flagged: number }[]>(`/api/ontology/users/${userId}/categories`),
    enabled: !!userId,
  });
  const listing = useQuery({
    queryKey: ["onto-desc", userId, category, status, query, sort, page],
    queryFn: () => api<Paged<DescriptionRow>>(
      `/api/ontology/users/${userId}/descriptions${qs({
        categoria: undefined, category, status, q: query, sort, page, page_size: 100 })}`),
    enabled: !!userId,
    placeholderData: (previous) => previous,
  });

  const mark = useMutation({
    mutationFn: ({ description, reviewed }: { description: string; reviewed: boolean }) =>
      api(`/api/review/${userId}/items`, { method: "PUT", json: { description, reviewed } }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["onto-desc"] });
      client.invalidateQueries({ queryKey: ["review-stats"] });
    },
  });

  const items = listing.data?.items ?? [];

  return (
    <>
      <PageHead eyebrow={`ontologia · ${userId?.slice(0, 8) ?? ""}`} title="Descrições e ontologia">
        O que a LLM extraiu de cada descrição, e onde a evidência discorda da categoria.
      </PageHead>

      {summary.data && (
        <Tiles items={[
          { key: "linhas", label: "Descrições", value: summary.data.rows, unit: "n" },
          { key: "ents", label: "Entidades", value: summary.data.entities, unit: "n" },
          { key: "sem", label: "Sem ontologia", value: summary.data.without_ontology,
            unit: "n", detail: pct(summary.data.without_ontology / summary.data.rows) },
          { key: "flag", label: "Sinalizadas", value: summary.data.flagged, unit: "n",
            flow: "DESPESA", detail: "evidência discorda da categoria" },
        ]} />
      )}

      <div style={{ marginTop: 18 }}>
        <ChartFrame
          title="O que a ontologia extraiu"
          sub="cobertura por tipo de entidade, empilhada por nível de confiança"
          guide="modelo"
          tableHeader={["tipo", "linhas", "% da base"]}
          tableRows={(summary.data?.types ?? []).map((t) =>
            [t.label, t.rows, `${t.coverage_pct ?? 0}%`])}
        >
          {summary.isPending && <Skeleton rows={11} height={16} />}
          {summary.data && (
            <CoverageBars rows={summary.data.types} total={summary.data.rows} />
          )}
        </ChartFrame>
      </div>

      <div className="controls" style={{ marginTop: 18 }}>
        <label>categoria
          <select value={category} onChange={(e) => set("category", e.target.value)}>
            <option value="">todas</option>
            {(categories.data ?? []).map((c) => (
              <option key={c.category} value={c.category}>
                {shortName(c.category)} ({c.rows})
              </option>
            ))}
          </select>
        </label>
        <label>situação
          <select value={status} onChange={(e) => set("status", e.target.value)}>
            <option value="all">todas</option>
            <option value="flagged">sinalizadas</option>
            <option value="high">severidade alta</option>
            <option value="pending">pendentes de revisão</option>
            <option value="no_ontology">sem ontologia</option>
          </select>
        </label>
        <input type="search" placeholder="Buscar descrição ou entidade…" value={query}
               aria-label="Buscar descrição ou entidade"
               onChange={(e) => set("q", e.target.value)} />
        <label>ordenar
          <select value={sort} onChange={(e) => set("ordem", e.target.value)}>
            <option value="amount">maior valor</option>
            <option value="review">prioridade de revisão</option>
            <option value="entities">mais entidades</option>
            <option value="description">descrição</option>
          </select>
        </label>
        <a className="btn" href={`/api/ontology/users/${userId}/descriptions.csv${qs({ category, status, q: query })}`}>
          baixar CSV
        </a>
      </div>

      <Card
        title="Descrições por categoria"
        sub={listing.data
          ? `${intBR(listing.data.total)} descrições · página ${listing.data.page} de ${listing.data.pages}`
          : undefined}
      >
        {listing.isPending && <Skeleton rows={12} />}
        {listing.error && <ErrorState error={listing.error} onRetry={() => listing.refetch()} />}
        {listing.data && !items.length && (
          <Empty onClear={() => setParams(new URLSearchParams())}>
            Nenhuma descrição corresponde ao filtro.
          </Empty>
        )}
        {!!items.length && listing.data && (
          <>
            <table>
              <thead>
                <tr>
                  <th scope="col">descrição</th>
                  <th scope="col">categoria</th>
                  <th scope="col" className="num">valor</th>
                  <th scope="col">entidades</th>
                  <th scope="col">revisar?</th>
                  <th scope="col">ok</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td style={{ maxWidth: 340 }}>
                      <span className="mono" style={{ fontSize: 12 }}>{row.description}</span>
                      {row.installments && (
                        <span className="tag" style={{ marginLeft: 6 }}>
                          {row.installments}x
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{shortName(row.category)}</td>
                    <td className="num">{cell(row.amount)}</td>
                    <td style={{ fontSize: 11.5, color: "var(--ink-3)", maxWidth: 260 }}>
                      {row.entities || (row.no_ontology ? "—" : "")}
                    </td>
                    <td>
                      {row.flag && (
                        <span className={`tag ${row.flag.severity}`}
                              title={`${row.flag.rule}: ${row.flag.evidence} aparece em ${row.flag.suggested_category} em ${row.flag.share_pct}% de ${row.flag.rows} linhas`}>
                          {row.flag.rule}
                          {row.flag.share_pct ? ` ${row.flag.share_pct}%` : ""}
                        </span>
                      )}
                    </td>
                    <td>
                      <input type="checkbox" checked={row.reviewed}
                             aria-label={`Marcar "${row.description}" como revisada`}
                             onChange={(e) => mark.mutate({
                               description: row.description, reviewed: e.target.checked })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="controls" style={{ marginTop: 14, justifyContent: "center" }}>
              <button className="btn" disabled={page <= 1}
                      onClick={() => set("pagina", String(page - 1))}>anterior</button>
              <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                {page} / {listing.data.pages}
              </span>
              <button className="btn" disabled={page >= listing.data.pages}
                      onClick={() => set("pagina", String(page + 1))}>próxima</button>
            </div>
          </>
        )}

        {summary.data && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--ink-3)" }}>
              como as linhas são sinalizadas
            </summary>
            <ul style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 8 }}>
              {Object.entries(summary.data.severity_spec.rules).map(([rule, text]) => (
                <li key={rule}><strong>{rule}</strong>: {text}</li>
              ))}
              <li><strong>severidade</strong>: {summary.data.severity_spec.severity}</li>
            </ul>
          </details>
        )}
      </Card>
    </>
  );
}
